import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardHeader,
  Container,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TablePagination,
  Typography,
} from '@mui/material';
import { ThreeDots } from 'react-loader-spinner';
import { Page } from '../../../components';
import Label from '../../../components/label';
import useUrlFilters from '../../../hooks/useUrlFilters';
import { fDateTime, fToNow } from '../../../utils/formatTime';
import { getNotifications, markAllAsRead, markAsRead } from '../store/notificationActions';

export const EMPTY_NOTIFICATION_FILTERS = { unread: '' };

const KIND_LABELS = {
  APPROVAL_NEEDED: { label: 'Approval needed', color: 'warning' },
  RECEIPT_NEEDED: { label: 'Receipt needed', color: 'warning' },
  NEXT_STEP: { label: 'Next step', color: 'info' },
  REQUEST_APPROVED: { label: 'Approved', color: 'success' },
  REQUEST_REJECTED: { label: 'Rejected', color: 'error' },
  REQUEST_COMPLETED: { label: 'Completed', color: 'success' },
};

/** The in-app feed -- the same notifications the mobile app shows. */
export default function Notifications() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, notifications, pagination, totalUnRead } = useSelector((state) => state.notificationReducer);
  const { filters, page, rowsPerPage, setPage, setRowsPerPage, applyFilters } =
    useUrlFilters(EMPTY_NOTIFICATION_FILTERS);

  useEffect(() => {
    dispatch(getNotifications({ page: page + 1, rowsPerPage, unread: filters.unread === 'true' }));
  }, [dispatch, page, rowsPerPage, filters]);

  const open = (notification) => {
    if (!notification.is_read) dispatch(markAsRead(notification.id));
    if (notification.request_id) navigate(`/dashboard/requests/${notification.request_id}`);
    else if (notification.audit_id) navigate(`/dashboard/audits/${notification.audit_id}`);
  };

  return (
    <Page title="Notifications">
      <Container maxWidth="md">
        <Card>
          <CardHeader
            title="Notifications"
            subheader={`${totalUnRead || 0} unread`}
            action={
              <Stack direction="row" spacing={1} alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.unread === 'true'}
                      onChange={(event) => applyFilters({ unread: event.target.checked ? 'true' : '' })}
                    />
                  }
                  label="Unread only"
                />
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!totalUnRead}
                  onClick={() => dispatch(markAllAsRead())}
                >
                  Mark all as read
                </Button>
              </Stack>
            }
          />
          {isLoading ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <ThreeDots height="80" width="80" radius="9" color="#2065d1" visible />
            </Stack>
          ) : (
            <Stack divider={<Divider />}>
              {notifications?.length ? (
                notifications.map((notification) => {
                  const kind = KIND_LABELS[notification.kind] || { label: notification.kind, color: 'default' };
                  return (
                    <Box
                      key={notification.id}
                      onClick={() => open(notification)}
                      sx={{
                        px: 3,
                        py: 2,
                        cursor: 'pointer',
                        bgcolor: notification.is_read ? 'transparent' : 'action.selected',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Stack direction="row" spacing={2} justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle2">{notification.title}</Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {notification.body}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.disabled' }}
                            title={fDateTime(notification.created_at)}
                          >
                            {fToNow(notification.created_at)}
                          </Typography>
                        </Box>
                        <Stack alignItems="flex-end" spacing={1}>
                          <Label color={kind.color}>{kind.label}</Label>
                          {!notification.is_read && (
                            <Button
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                dispatch(markAsRead(notification.id));
                              }}
                            >
                              Mark as read
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })
              ) : (
                <Typography variant="body2" sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                  No notifications.
                </Typography>
              )}
            </Stack>
          )}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={pagination?.count || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, next) => setPage(next)}
            onRowsPerPageChange={(event) => setRowsPerPage(parseInt(event.target.value, 10))}
          />
        </Card>
      </Container>
    </Page>
  );
}
